import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Lightbulb, Send, Bot, User } from "lucide-react";

const AdvisorView = () => {
  const { userId } = useParams();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm your AI Sales Advisor. I can help you with strategies for closing deals, prioritizing accounts, or any sales question you have. What would you like to discuss?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const suggestedQuestions = [
    "How do I close the deal faster?",
    "What accounts should I focus on this week?",
    "How can I improve my close ratio?",
    "What's the best approach for enterprise sales?"
  ];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/agent/insights`, {
        userId: userId,
        contextType: "advisor",
        context: {
          question: userMessage,
          conversationHistory: messages
        }
      });

      setMessages(prev => [
        ...prev,
        { role: "assistant", content: response.data.insights }
      ]);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to get response");
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: "I apologize, but I'm having trouble processing your request right now. Please try again." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInput(question);
  };

  return (
    <div className="space-y-6" data-testid="advisor-view">
      <div>
        <h1 className="text-2xl text-white mb-2 flex items-center gap-2">
          <Lightbulb className="h-6 w-6 text-neon-cyan" />
          <span className="text-neon-cyan">AI Advisor</span>
        </h1>
        <p className="text-sm text-gray-500">Strategic guidance for your sales process</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Interface */}
        <div className="lg:col-span-2">
          <Card className="glass-card rounded-xl h-[700px] flex flex-col">
            {/* Messages */}
            <ScrollArea className="flex-1 p-6" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-3 ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                    data-testid={`message-${idx}`}
                  >
                    {message.role === "assistant" && (
                      <div className="p-2 bg-[#3dd9c5]/10 rounded-full h-10 w-10 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-5 w-5 text-[#3dd9c5]" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg p-4 ${
                        message.role === "user"
                          ? "bg-[#3dd9c5] text-white"
                          : "bg-white/5 text-gray-300"
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                    {message.role === "user" && (
                      <div className="p-2 bg-white/10 rounded-full h-10 w-10 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3 justify-start">
                    <div className="p-2 bg-[#3dd9c5]/10 rounded-full h-10 w-10 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-[#3dd9c5]" />
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex gap-2">
                        <div className="w-2 h-2 bg-[#3dd9c5] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                        <div className="w-2 h-2 bg-[#3dd9c5] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                        <div className="w-2 h-2 bg-[#3dd9c5] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-gray-800">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !loading && handleSend()}
                  placeholder="Ask me anything about your sales strategy..."
                  className="bg-white/5 border-gray-700 text-white"
                  disabled={loading}
                  data-testid="advisor-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
                  data-testid="send-message-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Suggested Questions */}
          <Card className="bg-[#0f1419] border-gray-800/50 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Suggested Questions</h3>
            <div className="space-y-2">
              {suggestedQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestedQuestion(question)}
                  className="w-full text-left p-3 bg-white/5 hover:bg-white/10 border border-gray-700/50 hover:border-[#3dd9c5]/50 rounded-lg text-sm text-gray-300 transition-all"
                  data-testid={`suggested-q-${idx}`}
                >
                  {question}
                </button>
              ))}
            </div>
          </Card>

          {/* Quick Context */}
          <Card className="bg-[#0f1419] border-gray-800/50 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Quick Context</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-500 mb-1">Active Accounts</p>
                <p className="text-white font-semibold">5</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Pipeline Value</p>
                <p className="text-white font-semibold">$343K</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">This Week's Focus</p>
                <p className="text-[#3dd9c5] font-semibold">Close 2 deals</p>
              </div>
            </div>
          </Card>

          {/* Recent Topics */}
          <Card className="bg-[#0f1419] border-gray-800/50 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Topics</h3>
            <div className="space-y-2">
              {["Deal acceleration", "Account prioritization", "Objection handling"].map((topic, idx) => (
                <div
                  key={idx}
                  className="p-2 bg-[#3dd9c5]/10 border border-[#3dd9c5]/30 rounded text-xs text-[#3dd9c5]"
                >
                  {topic}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdvisorView;
